/**
 * The national RAS and penetration test program, firm by firm (Andres, 2026-09-23): where each firm is,
 * its milestone dates and what comes next, from CDW's security assessment tracker. Shown on the National
 * view for National IT and the Board, and as a single row on each firm's own dashboard.
 * Informative only: stages describe progress, with no red.
 */
import { PROGRAM_NAME } from '@grc/shared/leadership-measures';
import { Link } from 'react-router-dom';
import { ChartTable } from '../charts';
import { formatDate } from '../components';
import { useFrom } from '../origin';

export interface ProgramMilestone {
  milestone: 'kickoff' | 'pen_test' | 'pt_report' | 'risk_validation' | 'ras_report';
  status: 'complete' | 'in_progress' | 'not_started' | 'tbd';
  date: string | null;
  dateKind: 'actual' | 'target' | 'etc' | null;
  dateText: string | null;
}
export interface ProgramFirm {
  firmCode: string;
  firmName: string;
  wave: number | null;
  stage: 'scheduling_tbd' | 'scheduled' | 'in_progress' | 'complete';
  pctComplete: number | null;
  roadmapStatus: 'not_started' | 'in_progress' | 'complete' | null;
  roadmapNext: string | null;
  asOf: string;
  milestones: ProgramMilestone[];
}

export const MILESTONE_LABEL: Record<ProgramMilestone['milestone'], string> = {
  kickoff: 'Kickoff',
  pen_test: 'Penetration test',
  pt_report: 'Pen test report',
  risk_validation: 'Risk validation meeting',
  ras_report: 'Risk assessment report',
};

export const STAGE: Record<ProgramFirm['stage'], { label: string; tone: string }> = {
  complete: { label: 'Assessment complete', tone: 'in_place' },
  in_progress: { label: 'Assessment under way', tone: 'progress' },
  scheduled: { label: 'Scheduled', tone: 'neutral' },
  scheduling_tbd: { label: 'Dates to be confirmed', tone: 'neutral' },
};

const ROADMAP: Record<string, string> = { not_started: 'Not started', in_progress: 'Under way', complete: 'Complete' };


/** "Done Mar 10", "Estimated Sep 30", "Planned Oct 19", "Nov 2026" or "To be confirmed". */
export function milestoneText(m: ProgramMilestone): string {
  if (m.status === 'tbd') return 'To be confirmed';
  // One date form across the portal (DEC-23): "February 10, 2026".
  const when = m.date ? formatDate(m.date) : m.dateText;
  if (m.status === 'complete') return when ? `Done ${when}` : 'Done';
  if (m.status === 'in_progress') return when ? `Under way, estimated ${when}` : 'Under way';
  return when ? `Planned ${when}` : 'Not started';
}

/** The next thing that happens for a firm, in plain words. */
export function nextStep(f: ProgramFirm): string {
  const open = f.milestones.find((m) => m.status !== 'complete');
  if (open) {
    const text = milestoneText(open).replace(/^To be/, 'Dates to be');
    return `${MILESTONE_LABEL[open.milestone]}: ${text.charAt(0).toLowerCase()}${text.slice(1)}`;
  }
  if (f.roadmapStatus === 'complete') return 'Action plans agreed; remediation under way';
  if (f.roadmapStatus === 'in_progress') return `Roadmapping under way${f.roadmapNext ? `, next session ${f.roadmapNext}` : ''}`;
  return 'Roadmapping session to schedule';
}

function MilestoneCell({ m }: { m: ProgramMilestone }) {
  return (
    <span className={`ms ms-${m.status}`}>
      <span className="ms-dot" aria-hidden="true" />
      {milestoneText(m)}
    </span>
  );
}

function Progress({ pct, label }: { pct: number | null; label: string }) {
  return (
    <div className="prog-cell">
      <div className="prog-track" role="img" aria-label={label}>
        <span className="prog-fill" style={{ width: `${pct ?? 0}%` }} />
      </div>
      <span className="small">{pct === null ? '' : `${pct}%`}</span>
    </div>
  );
}

/** Firms by stage as one stacked bar, with each part labelled. */
export function StageBar({ firms }: { firms: ReadonlyArray<{ stage: string | null }> }) {
  const order: ProgramFirm['stage'][] = ['complete', 'in_progress', 'scheduled', 'scheduling_tbd'];
  const counts = order.map((s) => ({ s, n: firms.filter((f) => f.stage === s).length })).filter((c) => c.n);
  const total = firms.length || 1;
  return (
    <div className="stage-summary">
      <div className="stage-bar" role="img" aria-label={counts.map((c) => `${STAGE[c.s].label}: ${c.n}`).join(', ')}>
        {counts.map((c) => (
          <span key={c.s} className={`stage-seg stage-${c.s}`} style={{ width: `${(c.n / total) * 100}%` }} />
        ))}
      </div>
      <ul className="plain stage-legend">
        {counts.map((c) => (
          <li key={c.s}>
            <span className={`stage-key stage-${c.s}`} aria-hidden="true" />
            <strong>{c.n}</strong> {STAGE[c.s].label.toLowerCase()}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Kickoff to risk assessment report, one bar per firm with dates, and today marked. */
function ProgramTimeline({ firms, asOf }: { firms: ProgramFirm[]; asOf: string }) {
  const t = (s: string) => new Date(`${s}T00:00:00Z`).getTime();
  const span = (f: ProgramFirm) => {
    const dated = f.milestones.filter((m) => m.date).map((m) => t(m.date!));
    return dated.length ? { start: Math.min(...dated), end: Math.max(...dated) } : null;
  };
  const rows = firms.map((f) => ({ f, s: span(f) })).filter((r) => r.s);
  if (!rows.length) return null;
  const today = t(asOf);
  const min = Math.min(today, ...rows.map((r) => r.s!.start));
  const max = Math.max(today, ...rows.map((r) => r.s!.end));
  const from = Date.UTC(new Date(min).getUTCFullYear(), new Date(min).getUTCMonth(), 1);
  const to = Date.UTC(new Date(max).getUTCFullYear(), new Date(max).getUTCMonth() + 1, 1);
  const x = (ms: number) => ((ms - from) / (to - from)) * 100;
  const months: number[] = [];
  for (let d = from; d < to; d = Date.UTC(new Date(d).getUTCFullYear(), new Date(d).getUTCMonth() + 1, 1)) months.push(d);
  return (
    <div className="chart">
      <div className="roadmap program-timeline" role="img" aria-label="Assessment program timeline: kickoff to risk assessment report for each firm. The table lists every date.">
        <div className="roadmap-axis" aria-hidden="true">
          {months.map((m, i) => (
            <span key={m} style={{ left: `${x(m)}%` }}>
              {new Date(m).toLocaleDateString('en-CA', { month: 'short', ...(i === 0 || new Date(m).getUTCMonth() === 0 ? { year: '2-digit' } : {}), timeZone: 'UTC' })}
            </span>
          ))}
        </div>
        {rows.map(({ f, s }) => (
          <div className="roadmap-row" key={f.firmCode} title={`${f.firmName}: ${nextStep(f)}`}>
            <span className="roadmap-label">{f.firmName.replace(/^Baker Tilly /, '')}</span>
            <div className="roadmap-track">
              <span className="roadmap-today" style={{ left: `${x(today)}%` }} aria-hidden="true" />
              <div className={`roadmap-bar stage-${f.stage}`} style={{ left: `${x(s!.start)}%`, width: `${Math.max(x(s!.end) - x(s!.start), 1.5)}%` }} />
            </div>
          </div>
        ))}
      </div>
      <p className="muted small">Each bar runs from the first to the last dated milestone. The line marks the tracker date, {formatDate(asOf)}.</p>
    </div>
  );
}

export function ProgramCard({ program }: { program: { measure: string; asOf: string | null; firms: ProgramFirm[] } }) {
  const firms = program.firms;
  // A firm opened from here comes back here (CP-04).
  const from = useFrom();
  if (!firms.length) {
    return (
      <section className="card" aria-labelledby="program-h">
        <h2 id="program-h">{PROGRAM_NAME}</h2>
        <p className="muted">The program tracker has not been loaded yet.</p>
      </section>
    );
  }
  return (
    <section className="card program-card" aria-labelledby="program-h">
      <h2 id="program-h">{PROGRAM_NAME}</h2>
      <p className="muted small">
        Where each firm is in {PROGRAM_NAME}, and what comes next. Source: {program.measure}
        {program.asOf ? `, as of ${formatDate(program.asOf)}` : ''}.
      </p>
      <StageBar firms={firms} />
      <div className="table-wrap" tabIndex={0} role="region" aria-label={`${PROGRAM_NAME} by firm`}>
        <table className="program-table">
          <caption className="sr-only">Each firm's stage, progress, milestone dates, roadmapping and next step</caption>
          <thead>
            <tr>
              <th scope="col">Firm</th>
              <th scope="col">CDW round</th>
              <th scope="col">Stage</th>
              <th scope="col">Progress</th>
              {Object.values(MILESTONE_LABEL).map((l) => (
                <th scope="col" key={l}>
                  {l}
                </th>
              ))}
              <th scope="col">Roadmapping</th>
              <th scope="col">Next step</th>
            </tr>
          </thead>
          <tbody>
            {firms.map((f) => (
              <tr key={f.firmCode}>
                <th scope="row">
                  <Link to={`/firm/${f.firmCode}`} state={from}>{f.firmName}</Link>
                </th>
                <td>{f.wave ?? 'To be set'}</td>
                <td>
                  <span className={`chip tone-${STAGE[f.stage].tone}`}>{STAGE[f.stage].label}</span>
                </td>
                <td>
                  <Progress pct={f.pctComplete} label={`${f.firmName}: ${f.pctComplete ?? 0}% of the assessment complete`} />
                </td>
                {f.milestones.map((m) => (
                  <td key={m.milestone}>
                    <MilestoneCell m={m} />
                  </td>
                ))}
                <td>{f.roadmapStatus ? ROADMAP[f.roadmapStatus] : ''}</td>
                <td className="small">{nextStep(f)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {program.asOf && <ProgramTimeline firms={firms} asOf={program.asOf} />}
      <ChartTable
        caption={`${PROGRAM_NAME} by firm, as a plain table`}
        head={['Firm', 'CDW round', 'Stage', 'Progress', ...Object.values(MILESTONE_LABEL), 'Roadmapping', 'Next step']}
        rows={firms.map((f) => [f.firmName, f.wave === null ? 'To be set' : String(f.wave), STAGE[f.stage].label, `${f.pctComplete ?? 0}%`, ...f.milestones.map(milestoneText), f.roadmapStatus ? ROADMAP[f.roadmapStatus] : '', nextStep(f)])}
      />
    </section>
  );
}

/** One firm's place in the program, for its own dashboard. */
export function FirmProgramCard({ program }: { program: ProgramFirm & { measure: string } }) {
  return (
    <section className="card program-card" aria-labelledby="firm-program-h">
      <h2 id="firm-program-h">Risk assessment and pen test</h2>
      <p>
        <span className={`chip tone-${STAGE[program.stage].tone}`}>{STAGE[program.stage].label}</span>{' '}
        {program.wave !== null && <span className="muted small">Wave {program.wave}</span>}
      </p>
      {/* Five steps across, joined by a line, each with its date underneath. */}
      <ol className="plain stepper" aria-label="Program milestones">
        {program.milestones.map((m) => (
          <li key={m.milestone} className={`step ms-${m.status}`}>
            <span className="step-dot" aria-hidden="true" />
            <span className="step-name">{MILESTONE_LABEL[m.milestone]}</span>
            <span className="step-when">{milestoneText(m)}</span>
          </li>
        ))}
      </ol>
      <p className="small next-step">
        <strong>Next:</strong> {nextStep(program)}
      </p>
      <p className="muted small">
        {program.measure}, as of {formatDate(program.asOf)}.
      </p>
    </section>
  );
}
